import { createFileRoute } from "@tanstack/react-router";

import type { IntegrationStepConfig } from "../../../components/integrations/IntegrationPageShell";
import { ApiKeyPreviewField } from "../../../components/integrations/ApiKeyPreviewField";
import { CopyBlock } from "../../../components/integrations/CopyBlock";
import { IntegrationPageShell } from "../../../components/integrations/IntegrationPageShell";
import { useNubankCredentials } from "../../../components/integrations/useNubankCredentials";

export const Route = createFileRoute("/_authed/integrations/claude-code")({
  component: ClaudeCodeIntegrationPage,
  head: () => ({
    meta: [{ title: "Claude Code | NBDC" }],
  }),
});

const ENV_VAR_DESCRIPTIONS: { name: string; description: string }[] = [
  {
    name: "ANTHROPIC_BASE_URL",
    description:
      "Points Claude Code at the Nubank Anthropic-compatible gateway instead of the public Anthropic API.",
  },
  {
    name: "ANTHROPIC_AUTH_TOKEN",
    description:
      "Your NBDC API key. Replace the placeholder with the key you saved when you created or reset it.",
  },
  {
    name: "ANTHROPIC_CUSTOM_MODEL_OPTION",
    description:
      "The custom Nubank model to expose inside Claude Code's /model picker.",
  },
];

function ClaudeCodeIntegrationPage() {
  const { keyPreview, baseUrl, customModelId, isPending, errorMessage } =
    useNubankCredentials();

  const baseUrlExport = `export ANTHROPIC_BASE_URL="${baseUrl}"`;
  const apiKeyExport = 'export ANTHROPIC_AUTH_TOKEN="<YOUR_API_KEY>"';
  const customModelExport = customModelId
    ? `export ANTHROPIC_CUSTOM_MODEL_OPTION="${customModelId}"`
    : 'export ANTHROPIC_CUSTOM_MODEL_OPTION="<custom-model>"';

  const allExports = [baseUrlExport, apiKeyExport, customModelExport].join(
    "\n",
  );

  const steps: IntegrationStepConfig[] = [
    {
      title: "Set the Environment Variables",
      description:
        "Copy and paste this block into your shell, replacing <YOUR_API_KEY> with your key. To persist it across sessions, append the same lines to your ~/.bashrc or ~/.zshrc.",
      content: (
        <div className="flex flex-col gap-4">
          <CopyBlock
            language="bash"
            value={allExports}
            isLoading={isPending}
            errorMessage={errorMessage}
          />
          <ApiKeyPreviewField
            value={keyPreview}
            isLoading={isPending}
            errorMessage={errorMessage}
          />
          <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-3">
            <p className="text-xs font-medium text-foreground">
              What each variable does
            </p>
            <ul className="flex flex-col gap-1.5 text-xs text-muted-foreground">
              {ENV_VAR_DESCRIPTIONS.map(({ name, description }) => (
                <li key={name}>
                  <span className="font-mono text-foreground">{name}</span>{" "}
                  &mdash; {description}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-muted-foreground">
            If you added the lines to your shell profile, reload it with{" "}
            <span className="font-mono text-foreground">source ~/.zshrc</span>{" "}
            (or{" "}
            <span className="font-mono text-foreground">source ~/.bashrc</span>
            ).
          </p>
        </div>
      ),
    },
    {
      title: "Open Claude Code",
      description: "Start the Claude Code CLI in your terminal.",
      content: <CopyBlock language="bash" value="claude" />,
    },
    {
      title: "Pick the Custom Model",
      description:
        "Use the /model command inside Claude Code to switch to the custom model.",
      content: (
        <ol className="ml-5 list-decimal space-y-1 text-sm text-muted-foreground">
          <li>
            Type <span className="font-mono text-foreground">/model</span> in
            the Claude Code prompt
          </li>
          <li>Select the custom model from the list</li>
          <li>Start chatting with the model!</li>
        </ol>
      ),
    },
  ];

  return (
    <IntegrationPageShell
      title="Claude Code"
      description="Use the custom Nubank model from the Claude Code CLI."
      steps={steps}
    />
  );
}
